import { useEffect, useRef, useState } from "react";
import { translate, Uploader, type UploadCancelHandle, isFileAccepted } from "@kaltura/mediaspace-shared-utils";
import { Upload24Icon } from "@kaltura/ds-react-icons";
import styled from "@emotion/styled";
import { Button, ButtonProps } from "@kaltura/ds-react-components";
import { useConfig } from "@kaltura/mediaspace-shared-data-kms-config/useConfig";

export type UploadErrorReason = "invalidFileType" | "uploadError";

export interface UploadButtonProps {
    /**
     * text to show on button. default: "Upload file"
     */
    buttonText?: string;
    /**
     * accepted file types
     */
    accept?: string;
    /**
     * flag to indicate the uploaded is being processed
     * keeps the upload button in loading state until it is done
     */
    uploadProcessing?: boolean;
    /**
     * function to call when the file finished uploading (upload token created)
     */
    onFileUploaded: (uploadToken: string) => void;
    /**
     * function to call when upload error occurred (upload token not created).
     * @param reason optional reason for the error
     */
    onUploadError: (reason?: UploadErrorReason) => void;

    /**
     * function to call when upload process started
     */
    onUploadStarted?: () => void;

    /**
     * function to call to report upload progress
     * @param loaded
     * @param total
     */
    onProgress?: (loaded: number, total: number) => void;

    className?: string;

    color?: ButtonProps["color"];

    ks?: string;
    serviceUrl?: string;
    hideProcessingState?: boolean;

    /**
     * retrieve cancel handle.
     * cancel handle allows canceling the upload process,
     * which will prevent all callbacks from being triggered.
     * @param handle
     */
    onCancelHandleReady?: (handle: UploadCancelHandle) => void;
}

const HiddenInput = styled.input`
    visibility: hidden;
    display: none;
`;

const StyledButton = styled(Button)(({ theme }) => ({
    minWidth: theme.typography.pxToRem(111),
}));

/**
 * Generic upload button component for uploading a single file.
 * Supports configurable accepted file types, button text, and upload lifecycle callbacks.
 */
export const UploadButton = ({
    color = "primary",
    className = "",
    accept,
    buttonText,
    uploadProcessing,
    onFileUploaded,
    onUploadError,
    onUploadStarted,
    onProgress,
    serviceUrl: serviceUrlProp,
    ks,
    hideProcessingState,
    onCancelHandleReady,
}: UploadButtonProps) => {
    const fileUploadRef = useRef<HTMLInputElement>(null);

    const {
        application: { serviceUrl, uploadKS },
    } = useConfig();
    const [state, setState] = useState({
        uploading: false,
        total: 0,
    });

    useEffect(() => {
        setState({
            uploading: false,
            total: 0,
        });
        //reset value to allow uploading the same file again
        if (fileUploadRef.current) {
            fileUploadRef.current.value = "";
        }
    }, [uploadProcessing]);

    const uploadFile = () => {
        const effectiveKs = ks || uploadKS;
        const effectiveServiceUrl = serviceUrlProp || serviceUrl;
        if (!effectiveKs || !effectiveServiceUrl) {
            console.error("missing upload params");
            return;
        }
        const file = fileUploadRef.current && fileUploadRef.current.files ? fileUploadRef.current.files[0] : null;
        const uploader = new Uploader();
        if (file) {
            if (!isFileAccepted(file, accept)) {
                if (fileUploadRef.current) {
                    fileUploadRef.current.value = "";
                }
                onUploadError("invalidFileType");
                return;
            }

            const cancelHandle = uploader.upload({
                serviceUrl: effectiveServiceUrl,
                ks: effectiveKs,
                file: file,
                onError: handleError,
                onUploadDone: handleUploadDone,
                onProgress: onProgress,
            });
            onCancelHandleReady?.(cancelHandle);
            setState({
                uploading: true,
                total: file.size,
            });
            onUploadStarted?.();
        }
    };

    /**
     * user requested to upload a new file,
     * show file browser
     */
    const onUpload = () => {
        if (!fileUploadRef || !fileUploadRef.current) {
            return;
        }

        fileUploadRef.current.click();
    };

    /**
     * successful upload
     * submit upload token id to KMS and reset uploader state
     */
    const handleUploadDone = (uploadToken: string) => {
        setState({
            uploading: false,
            total: 0,
        });
        // reset value to allow uploading the same file again
        if (fileUploadRef.current) {
            fileUploadRef.current.value = "";
        }
        onFileUploaded(uploadToken);
    };

    /**
     * upload error or entry creation error,
     * notify user
     * @param error
     */
    const handleError = (error: string) => {
        setState({
            uploading: false,
            total: 0,
        });
        //reset value to allow uploading the same file again
        if (fileUploadRef.current) {
            fileUploadRef.current.value = "";
        }
        onUploadError("uploadError");
    };

    return (
        <>
            <StyledButton
                color={color}
                startIcon={<Upload24Icon />}
                loading={!hideProcessingState && (state.uploading || uploadProcessing)}
                disabled={state.uploading || uploadProcessing}
                onClick={onUpload}
                className={`kms-ds-upload-button ${className}`}
            >
                {buttonText ?? translate("Upload File")}
            </StyledButton>
            <HiddenInput ref={fileUploadRef} type="file" name="upload" accept={accept} onChange={uploadFile} />
        </>
    );
};
